/**
 * DoctrineHorizon — H5 section for Methodology.tsx
 * W66.X17 — Surfaces methodology-as-engine doctrine and reference links inline.
 *
 * Renders a doctrine summary pulled from canonical copy + links to the full
 * Reference Library docs. NOT a static prose page — this is a live section
 * that gives the Methodology Workspace its voice.
 *
 * Canon guardrails (CANON_VISUALS_LOCKED.md 2026-05-29):
 *   5-color palette. No exclamation points. No emojis. M-PACT hyphenated.
 *   Bryan K. Short, Founder and CEO, Mi6 Group.
 *
 * W66.X17 locked: 2026-05-31
 */

import { useLocation } from "wouter";
import { methodologyAsEngine } from "@/content/index";
import MarkdownRenderer from "@/components/content/MarkdownRenderer";

const RED = "#DC2626";
const BLACK = "#0B0B0F";

interface DocLink {
  slug: string;
  label: string;
  hint: string;
}

const DOC_LINKS: DocLink[] = [
  {
    slug: "methodology-as-engine",
    label: "Methodology as Engine",
    hint: "Full doctrine: Sales Plan, three horizons, Operator Test.",
  },
  {
    slug: "attilius-why-different",
    label: "Why Attilius Is Different",
    hint: "The structural distinction between intelligence platform and CRM.",
  },
  {
    slug: "cp1-project",
    label: "CP1 — Project",
    hint: "Entry checkpoint model — seven questions, scoring logic.",
  },
  {
    slug: "cp2-need-pain-demonstration",
    label: "CP2 — Need / Pain / Demonstration",
    hint: "20-point gravitational center — nine questions, the doctor frame.",
  },
  {
    slug: "cp3-budget",
    label: "CP3 — Budget",
    hint: "Three-state gate: Formal, Discretionary, No budget yet.",
  },
  {
    slug: "cp4-cp10-summaries",
    label: "CP4 through CP10",
    hint: "Execution half: Timeline, Authority, Sign-off, Org Map, Competition, Compliance, Legal.",
  },
];

export default function DoctrineHorizon() {
  const [, setLocation] = useLocation();

  return (
    <div>
      {/* Pull-quote intro */}
      <div
        style={{
          background: "#0B0B0F",
          color: "#FFFFFF",
          borderRadius: 10,
          padding: "28px 32px",
          marginBottom: 32,
          maxWidth: 720,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: RED,
            marginBottom: 12,
          }}
        >
          Operating Doctrine
        </div>
        <p
          style={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: 18,
            lineHeight: 1.5,
            color: "#FFFFFF",
            margin: "0 0 12px",
            fontWeight: 400,
          }}
        >
          The platform's job is to make three things — the Sales Plan, the methodology,
          and the daily operating surface — one thing. Not three.
        </p>
        <p
          style={{
            fontSize: 12,
            color: "#9CA3AF",
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          Bryan K. Short, Founder and CEO, Mi6 Group
        </p>
      </div>

      {/* M-PACT pillars — abbreviated from canonical copy */}
      <div
        style={{
          marginBottom: 32,
          padding: "20px 24px",
          background: "#FAFAF7",
          border: "1px solid #E5E5E7",
          borderRadius: 10,
          maxWidth: 720,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "#6B6B70",
            marginBottom: 16,
          }}
        >
          M-PACT — What It Stands For
        </div>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
          <tbody>
            {[
              ["M", "Methodology", "The defined way of selling Mi6 Group has built and trained for nearly two decades."],
              ["P", "Process / Prioritization", "Knowing what to work on, in what order, and what to put down. Process without prioritization is busywork."],
              ["A", "Accountability", "Team members own their numbers, accounts, and commitments. The system makes that ownership visible."],
              ["C", "Cadence / Coaching", "A particular cadence of events has to be followed to succeed. Ongoing coaching maximizes every checkpoint."],
              ["T", "Timing / Timeline", "Being in the right place at the right time, not on the outside looking in, not wasting time on a deal whose timing will never work."],
            ].map(([letter, word, desc]) => (
              <tr key={letter} style={{ borderBottom: "1px solid #F3F4F6" }}>
                <td
                  style={{
                    padding: "8px 12px 8px 0",
                    fontWeight: 800,
                    fontSize: 16,
                    color: RED,
                    fontFamily: "Georgia, serif",
                    verticalAlign: "top",
                    width: 28,
                  }}
                >
                  {letter}
                </td>
                <td
                  style={{
                    padding: "8px 16px 8px 0",
                    fontWeight: 700,
                    color: BLACK,
                    verticalAlign: "top",
                    whiteSpace: "nowrap",
                    fontSize: 13,
                  }}
                >
                  {word}
                </td>
                <td
                  style={{
                    padding: "8px 0",
                    color: "#525258",
                    lineHeight: 1.55,
                    verticalAlign: "top",
                    fontSize: 13,
                  }}
                >
                  {desc}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p
          style={{
            marginTop: 14,
            fontSize: 12,
            color: "#6B6B70",
            fontStyle: "italic",
            margin: "14px 0 0",
          }}
        >
          Methodology is the master. The other four are how it runs every week. Buy any
          one in isolation and it regresses by Q+2. M-PACT is the integrated answer.
        </p>
      </div>

      {/* Accelerated Jumpstart milestones */}
      <div
        style={{
          marginBottom: 32,
          maxWidth: 720,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "#6B6B70",
            marginBottom: 14,
          }}
        >
          Accelerated Jumpstart — 90-Day Engagement Program
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 12,
          }}
        >
          {[
            {
              horizon: "Jumpstart Day 30",
              cadence: "Days 1–30",
              desc: "Coverage and diagnosis. CP1-CP4 complete on priority accounts. Top-10 identified and scored. Coaching cadence active.",
            },
            {
              horizon: "Jumpstart Day 60",
              cadence: "Days 31–60",
              desc: "Advancement and gap closure. Weakest checkpoints addressed. Buying-committee gaps mapped. Forecast commitments validated.",
            },
            {
              horizon: "Jumpstart Day 90",
              cadence: "Days 61–90",
              desc: "Program close. Pipeline health reviewed. Accounts promoted or qualified out. Transition to full annual-plan operating cadence.",
            },
          ].map(({ horizon, cadence, desc }) => (
            <div
              key={horizon}
              style={{
                background: "#FFFFFF",
                border: "1px solid #E5E5E7",
                borderRadius: 8,
                padding: "16px 18px",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: BLACK,
                  marginBottom: 4,
                }}
              >
                {horizon}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: RED,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  marginBottom: 8,
                }}
              >
                {cadence}
              </div>
              <p style={{ fontSize: 12.5, color: "#525258", lineHeight: 1.55, margin: 0 }}>
                {desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Reference Library links */}
      <div style={{ maxWidth: 720 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "#6B6B70",
            marginBottom: 12,
            paddingBottom: 8,
            borderBottom: "1px solid #E5E5E7",
          }}
        >
          Full Reference Documents
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 10,
          }}
        >
          {DOC_LINKS.map((doc) => (
            <button
              key={doc.slug}
              type="button"
              onClick={() => setLocation(`/library/${doc.slug}`)}
              style={{
                textAlign: "left",
                background: "#FFFFFF",
                border: "1px solid #E5E5E7",
                borderRadius: 8,
                padding: "12px 16px",
                cursor: "pointer",
                transition: "border-color 0.15s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = RED;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = "#E5E5E7";
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 13,
                  color: BLACK,
                  marginBottom: 4,
                }}
              >
                {doc.label}
              </div>
              <div style={{ fontSize: 12, color: "#6B6B70", lineHeight: 1.45 }}>
                {doc.hint}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
